import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { Deal } from '@/hooks/useDeals';

export interface EligibilityCriteria {
  min_followers?: number;
  max_followers?: number;
  sports?: string[];
  tiers?: string[];
  competition_levels?: string[];
  positions?: string[];
  nationalities?: string[];
  min_age?: number;
  max_age?: number;
  verified_only?: boolean;
}

export interface MarketplaceDeal extends Deal {
  brand_profile_id?: string;
  brand_name?: string;
  brand_logo_url?: string;
  is_claimable: boolean;
  claimed_by_profile_id?: string;
  claimed_at?: string;
  requirements?: string[];
  deadline?: string;
  eligibility_criteria?: EligibilityCriteria;
  user_application_status?: string | null;
  user_application_id?: string | null;
  application_initiated_by?: 'talent' | 'brand' | 'venture';
  posted_by?: string;
}


export const useMarketplaceDeals = () => {
  const [deals, setDeals] = useState<MarketplaceDeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);
  const { currentUser, accountType } = useUser();

  // Helper function to calculate age from date of birth
  const calculateAge = (dateOfBirth: string | null): number | null => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Helper function to check if talent meets deal criteria
  const talentMeetsCriteria = (talentData: any, criteria: EligibilityCriteria | null): boolean => {
    if (!criteria || Object.keys(criteria).length === 0) return true; // No criteria = open to all
    if (!talentData) return false;

    // Followers check - treat 0 followers as "not yet tracked" and allow them through
    const followers = talentData.total_followers || 0;
    if (criteria.min_followers && followers > 0 && followers < criteria.min_followers) return false;
    if (criteria.max_followers && followers > criteria.max_followers) return false;

    // Sport check
    if (criteria.sports?.length && !criteria.sports.includes(talentData.sport)) return false;

    // Tier check
    if (criteria.tiers?.length && !criteria.tiers.includes(talentData.tier)) return false;

    // Competition level check
    if (criteria.competition_levels?.length && !criteria.competition_levels.includes(talentData.competition_level)) return false;

    // Position check
    if (criteria.positions?.length && talentData.position && !criteria.positions.includes(talentData.position)) return false;

    // Nationality check
    if (criteria.nationalities?.length && talentData.nationality && !criteria.nationalities.includes(talentData.nationality)) return false;

    // Age check
    if (criteria.min_age || criteria.max_age) {
      const age = calculateAge(talentData.date_of_birth);
      if (age !== null) {
        if (criteria.min_age && age < criteria.min_age) return false;
        if (criteria.max_age && age > criteria.max_age) return false;
      }
    }

    // Verified check
    if (criteria.verified_only && !talentData.is_verified) return false;

    return true; // Passed all checks
  };

  const fetchMarketplaceDeals = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch deals from database
      const { data: dealsData, error: dealsError } = await supabase
        .from('deals')
        .select('*')
        .in('status', ['available', 'pending', 'claimed', 'active']);

      if (dealsError) {
        throw dealsError;
      }

      // Get brand information separately for deals that have brand_profile_id
      const brandIds = dealsData?.map(deal => deal.brand_profile_id).filter(Boolean) || [];
      let brandsData: any[] = [];

      if (brandIds.length > 0) {
        const { data: brands, error: brandsError } = await supabase
          .from('brands')
          .select('id, profile_id, company_name, logo_url')
          .in('profile_id', brandIds);

        if (!brandsError) {
          brandsData = brands || [];
        }
      }


      // Get current user's profile ID and talent data for filtering
      let currentUserProfileId: string | null = null;
      let currentTalentData: any = null;

      if (currentUser?.user_id) {
        // Fetch profile ID from user_id
        const { data: profileData } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', currentUser.user_id)
          .maybeSingle();

        if (profileData) {
          currentUserProfileId = profileData.id;
          setProfileId(currentUserProfileId);

          if (accountType === 'talent') {
            // Fetch current talent's complete profile for criteria matching
            const { data: talentData } = await supabase
              .from('talents')
              .select(`
                *,
                profiles!inner(is_verified, display_name)
              `)
              .eq('profile_id', currentUserProfileId)
              .maybeSingle();

            if (talentData) {
              currentTalentData = {
                ...talentData,
                is_verified: talentData.profiles?.is_verified || false
              };
              console.log('Talent data loaded:', { currentUserProfileId, followers: currentTalentData.total_followers });
            }
          }
        }
      }

      // Fetch user's applications
      let userApplications: any[] = [];
      if (currentUserProfileId) {
        if (accountType === 'talent') {
          const { data: appsData } = await supabase
            .from('deal_applications')
            .select('id, deal_id, status, initiated_by')
            .eq('talent_profile_id', currentUserProfileId);

          if (appsData) {
            userApplications = appsData;
          }
        } else if (accountType === 'brand') {
          const { data: appsData } = await supabase
            .from('deal_applications')
            .select('id, deal_id, status, initiated_by')
            .eq('brand_profile_id', currentUserProfileId);

          if (appsData) {
            userApplications = appsData;
          }
        }
      }

      // Transform database deals to marketplace deals format with application data
      const marketplaceDeals: MarketplaceDeal[] = (dealsData || []).map(deal => {
        const brand = brandsData.find(b => b.profile_id === deal.brand_profile_id);
        const userApplication = userApplications.find(app => app.deal_id === deal.id);

        return {
          ...deal,
          brand_name: brand?.company_name || '',
          brand_logo_url: brand?.logo_url || '',
          is_claimable: deal.status === 'available' && !deal.talent_profile_id,
          claimed_by_profile_id: deal.talent_profile_id || undefined,
          claimed_at: deal.talent_profile_id ? deal.updated_at : undefined,
          requirements: [],
          deadline: deal.estimated_completion_date || undefined,
          actual_completion_date: deal.actual_completion_date || '',
          eligibility_criteria: (deal.eligibility_criteria as EligibilityCriteria) || undefined,
          user_application_status: userApplication?.status || null,
          user_application_id: userApplication?.id || null,
          application_initiated_by: userApplication?.initiated_by || undefined,
          posted_by: deal.posted_by || 'brand',
        };
      });

      // Filter based on user role
      let filteredDeals = marketplaceDeals;
      
      console.log('Total marketplace deals fetched:', marketplaceDeals.length);
      
      if (accountType === 'brand') {
        // Brands see all deals including their own
        filteredDeals = marketplaceDeals;
      } else if (accountType === 'talent' && currentUserProfileId) {
        // Talents see available deals that match their criteria + their active deals + applied deals
        filteredDeals = marketplaceDeals.filter(deal => {
          // Show deals where user has an application
          if (deal.user_application_status) {
            console.log('Including deal with application:', deal.title, deal.user_application_status);
            return true;
          }
          
          // Always show their own active/pending deals
          if ((deal.status === 'active' || deal.status === 'pending') && deal.talent_profile_id === currentUserProfileId) {
            console.log('Including own deal:', deal.title);
            return true;
          }
          
          // Exclude talent-posted deals entirely
          if (deal.posted_by === 'talent') {
            return false;
          }

          // For available deals, check if talent meets criteria
          if (deal.status === 'available' && !deal.talent_profile_id) {
            const meetsCriteria = talentMeetsCriteria(currentTalentData, deal.eligibility_criteria || null);
            console.log('Deal eligibility check:', { 
              deal: deal.title, 
              meetsCriteria, 
              criteria: deal.eligibility_criteria,
              talentFollowers: currentTalentData?.total_followers 
            });
            return meetsCriteria;
          }
          
          return false;
        });
      } else {
        // Agents and others see available and pending deals
        filteredDeals = marketplaceDeals.filter(deal => 
          deal.status === 'available' || deal.status === 'pending'
        );
      }
      
      setDeals(filteredDeals);
    } catch (err) {
      console.error('Error fetching marketplace deals:', err);
      setError('Failed to load marketplace deals');
    } finally {
      setLoading(false);
    }
  };

  const claimDeal = async (dealId: string) => {
    if (!currentUser?.user_id) {
      throw new Error('Must be logged in to apply for deals');
    }

    try {
      // Get profile ID
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', currentUser.user_id)
        .maybeSingle();

      if (profileError || !profileData) {
        throw new Error('Profile not found');
      }

      // Determine application data based on account type
      const applicationData: any = {
        deal_id: dealId,
        status: 'pending',
      };

      if (accountType === 'talent') {
        // Talent applying to a brand-posted deal
        applicationData.talent_profile_id = profileData.id;
        applicationData.initiated_by = 'talent';
      } else {
        throw new Error('Invalid account type for this action');
      }

      const { data: insertData, error: insertError } = await supabase
        .from('deal_applications')
        .insert(applicationData)
        .select('id')
        .single();

      if (insertError) {
        if (insertError.code === '23505') {
          throw new Error('You have already applied for this deal');
        }
        throw insertError;
      }

      await fetchMarketplaceDeals();
      return { success: true, applicationId: insertData.id };
    } catch (err: any) {
      console.error('Error applying for deal:', err);
      throw err;
    }
  };

  const acceptTalentApplication = async (dealId: string, talentProfileId: string) => {
    if (!currentUser?.id) {
      throw new Error('Must be logged in to accept applications');
    }

    try {
      const { error: updateError } = await supabase
        .from('deals')
        .update({
          status: 'active',
          updated_at: new Date().toISOString(),
        })
        .eq('id', dealId)
        .eq('status', 'pending')
        .eq('talent_profile_id', talentProfileId);

      if (updateError) {
        throw updateError;
      }

      await fetchMarketplaceDeals();
      return { success: true };
    } catch (err) {
      console.error('Error accepting application:', err);
      throw new Error('Failed to accept application');
    }
  };

  const rejectTalentApplication = async (dealId: string) => {
    if (!currentUser?.id) {
      throw new Error('Must be logged in to reject applications');
    }

    try {
      const { error: updateError } = await supabase
        .from('deals')
        .update({
          status: 'available',
          talent_profile_id: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', dealId)
        .eq('status', 'pending');

      if (updateError) {
        throw updateError;
      }

      await fetchMarketplaceDeals();
      return { success: true };
    } catch (err) {
      console.error('Error rejecting application:', err);
      throw new Error('Failed to reject application');
    }
  };

  const getMyClaimedDeals = () => {
    if (!currentUser?.user_id || accountType !== 'talent') {
      return [];
    }
    
    return deals.filter(deal => 
      // Only show talent-initiated applications
      (deal.user_application_status === 'pending' && deal.application_initiated_by === 'talent') ||
      (deal.user_application_status === 'accepted' && deal.application_initiated_by === 'talent') ||
      // Or active deals assigned to them that were talent-initiated
      ((deal.status === 'pending' || deal.status === 'claimed' || deal.status === 'active') &&
       deal.talent_profile_id !== null &&
       deal.application_initiated_by === 'talent')
    );
  };

  const getPostedDeals = () => {
    if (!profileId) return [];
    if (accountType === 'brand') {
      return deals.filter(deal =>
        deal.brand_profile_id === profileId && deal.posted_by !== 'talent'
      );
    }
    return [];
  };

  const getAvailableDeals = () => {
    return deals.filter(deal => {
      const isAvailableAndClaimable = deal.status === 'available' &&
        deal.is_claimable &&
        !deal.user_application_status;

      if (!isAvailableAndClaimable) return false;

      // Only show brand-posted deals
      return deal.posted_by !== 'talent';
    });
  };

  const getActiveDeals = () => {
    return deals.filter(deal => deal.status === 'active');
  };

  const getPendingDeals = () => {
    if (accountType === 'talent') {
      // For talents, show only brand-initiated pending applications
      return deals.filter(deal => 
        deal.user_application_status === 'pending' && 
        deal.application_initiated_by === 'brand'
      );
    }
    
    if (accountType === 'brand') {
      // For brands, show their deals with pending applications
      // Note: deals are filtered in fetchMarketplaceDeals to only show brand's own deals
      return deals.filter(deal => deal.status === 'pending');
    }
    
    // For ventures, show all pending deals
    return deals.filter(deal => deal.status === 'pending');
  };

  useEffect(() => {
    if (currentUser?.user_id) {
      fetchMarketplaceDeals();
    }
  }, [currentUser?.user_id, accountType]);

  return {
    deals,
    loading,
    error,
    claimDeal,
    acceptTalentApplication,
    rejectTalentApplication,
    getMyClaimedDeals,
    getPostedDeals,
    getAvailableDeals,
    getActiveDeals,
    getPendingDeals,
    fetchMarketplaceDeals,
  };
};
